/**
 * Build DHTMLX schedule-grid columns from runtime context.
 * Column config lives here — not in GanttPage.
 */
import {
  buildAssigneeAvatarsHtml,
  formatTaskAssigneesSummary,
  resolveAssignmentsForTask,
} from '../../features/schedule-tasks/components/assignments/assignDisplayUtils';
import { PERMISSION_RESTRICTED_MESSAGE } from '../../permissions/scheduler';
import { normalizeTaskType, resolveTaskName } from '../../utils/utils';
import {
  computeCurrentVsBaselineVariance,
  formatVarianceDays,
  getTaskBaseline,
} from '../baselineCompareUtils';
import { formatDependencyLinkToken } from '../dependencyDisplayUtils';
import { wrapPendingChangeCell } from '../editedFieldKeys';
import { formatGanttDateFromStored, formatGanttDateLabel } from '../ganttDateFormat';
import {
  DEFAULT_GANTT_COLUMN_WIDTHS,
  resolveGanttColumnWidth,
} from '../ganttColumnWidths';
import { TASK_TYPE_OPTIONS } from './taskTypeOptions';
import type { BuildGanttColumnsContext } from './types';

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Column header for a baseline variance: a delta mark, not a repeated field name. */
function baselineDeltaHeader(title: string): string {
  return (
    `<span class="gantt-baseline-delta-head" title="${escapeHtml(title)}" aria-label="${escapeHtml(title)}">` +
    `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">` +
    `<path d="M4 19h16L12 4z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>` +
    `</svg></span>`
  );
}

function baselineVarianceCell(
  days: number | null,
  emptyTitle: string,
  describe: (days: number) => string,
): string {
  const label = escapeHtml(formatVarianceDays(days));
  if (days == null) {
    return `<span class="gantt-baseline-var is-na" title="${escapeHtml(emptyTitle)}">${label}</span>`;
  }
  const cls =
    days > 0
      ? 'gantt-baseline-var is-late'
      : days < 0
        ? 'gantt-baseline-var is-early'
        : 'gantt-baseline-var is-even';
  return `<span class="${cls}" title="${escapeHtml(describe(days))}">${label}</span>`;
}

export function buildGanttColumns(ctx: BuildGanttColumnsContext): any[] {
  const {
    projectId,
    store,
    columnWidthsRef,
    visibleOptionalColumns,
    selectedAssignTaskIdsRef,
    pendingEditedFieldsRef,
    accumulatedChangesRef,
    ganttRef,
    parentIdsWithChildren,
    canTaskCreate,
    canTaskDelete,
    effectiveLocationIdByTaskIdRef,
    locationIdToNameRef,
    locationModalTaskIdRef,
    predecessorPickerTaskIdRef,
    predSuccLinkIndex,
    trackedLeafTaskIds,
    liveAssignmentsRef,
    compareBaselinesEnabled,
    compareBaselineIndex,
  } = ctx;

  const baselineCompare = compareBaselinesEnabled && compareBaselineIndex != null;
  const baselineIndex = compareBaselineIndex;

  const columnWidth = (name: string, fallback: number) =>
    resolveGanttColumnWidth(name, columnWidthsRef.current, projectId, fallback);

  const startColumn = {
    name: 'start_date',
    label: 'Start',
    align: 'center' as const,
    width: columnWidth('start_date', DEFAULT_GANTT_COLUMN_WIDTHS.start_date),
    min_width: 110,
    // Prefer store start (backend / Task Details). dhtmlx work_time can shift
    // task.start_date without an exclusive↔inclusive bridge.
    template: (task: any) => {
      const snap = store.getTask(String(task.id));
      return wrapPendingChangeCell(
        !!pendingEditedFieldsRef.current.get(String(task.id))?.has('start_date'),
        formatGanttDateLabel(snap?.start ?? task.start_date),
      );
    },
    resize: true,
    editor: { type: 'date', map_to: 'start_date' },
  };

  const finishColumn = {
    name: 'end_date',
    label: baselineCompare ? 'Finish' : 'End',
    align: 'center' as const,
    width: columnWidth('end_date', DEFAULT_GANTT_COLUMN_WIDTHS.end_date),
    min_width: 110,
    // Prefer store finish (inclusive backend day). Raw gantt end_date is exclusive
    // under work_time and can show n−1 vs Task Details.
    template: (task: any) => {
      const snap = store.getTask(String(task.id));
      return wrapPendingChangeCell(
        !!pendingEditedFieldsRef.current.get(String(task.id))?.has('end_date'),
        formatGanttDateLabel(snap?.finish ?? task.end_date),
      );
    },
    resize: true,
    editor: { type: 'date', map_to: 'end_date' },
  };

  const durationColumn = {
    name: 'duration',
    label: 'Duration',
    align: 'center' as const,
    width: columnWidth('duration', DEFAULT_GANTT_COLUMN_WIDTHS.duration),
    resize: true,
    // Prefer store duration (same as Task Details). ganttRef.current?.instance?.calculateDuration uses
    // exclusive end_date + work hours and often yields API duration − 1 for leaves.
    template: (task: any) => {
      const snap = store.getTask(String(task.id));
      const value = snap?.duration ?? task.duration;
      const n = Number(value);
      const label = Number.isFinite(n) ? String(Math.round(n)) : '';
      return wrapPendingChangeCell(
        !!pendingEditedFieldsRef.current.get(String(task.id))?.has('duration'),
        label,
      );
    },
    editor: { type: 'number', map_to: 'duration', min: 0, max: 9999 },
  };

  const baselineDateColumn = (name: string, label: string, field: 'start' | 'finish') => ({
    name,
    label,
    align: 'center' as const,
    width: columnWidth(name, DEFAULT_GANTT_COLUMN_WIDTHS.start_date),
    min_width: 110,
    resize: true,
    template: (task: any) => {
      const snap = store.getTask(String(task.id));
      const entry = baselineIndex == null ? null : getTaskBaseline(snap, baselineIndex);
      const raw = field === 'start' ? entry?.start : entry?.finish;
      return formatGanttDateFromStored(raw ?? null);
    },
  });

  const baselineDurationValueColumn = {
    name: 'baselineDuration',
    label: 'Baseline Duration',
    align: 'center' as const,
    width: columnWidth('baselineDuration', DEFAULT_GANTT_COLUMN_WIDTHS.duration),
    resize: true,
    template: (task: any) => {
      const snap = store.getTask(String(task.id));
      const entry = baselineIndex == null ? null : getTaskBaseline(snap, baselineIndex);
      const n = Number(entry?.duration);
      return Number.isFinite(n) ? String(Math.round(n)) : '';
    },
  };

  const varianceColumn = (
    name: string,
    title: string,
    pick: 'startDays' | 'finishDays' | 'durationDays',
    emptyTitle: string,
    describe: (days: number) => string,
  ) => ({
    name,
    label: baselineDeltaHeader(title),
    align: 'center' as const,
    width: columnWidth(name, DEFAULT_GANTT_COLUMN_WIDTHS.baselineStartVar),
    resize: true,
    template: (task: any) => {
      const snap = store.getTask(String(task.id));
      const variance =
        baselineIndex == null
          ? { startDays: null, finishDays: null, durationDays: null }
          : computeCurrentVsBaselineVariance(snap, baselineIndex);
      return baselineVarianceCell(variance[pick], emptyTitle, describe);
    },
  });

  const scheduleCoreColumns = baselineCompare
    ? [
        baselineDateColumn('baselineStart', 'Baseline Start', 'start'),
        startColumn,
        varianceColumn(
          'baselineStartVar',
          'Start variance',
          'startDays',
          'No baseline start date',
          (days) =>
            days > 0
              ? `${days} day(s) later than baseline`
              : days < 0
                ? `${Math.abs(days)} day(s) earlier than baseline`
                : 'Same start as baseline',
        ),
        baselineDateColumn('baselineFinish', 'Baseline Finish', 'finish'),
        finishColumn,
        varianceColumn(
          'baselineFinishVar',
          'Finish variance',
          'finishDays',
          'No baseline finish date',
          (days) =>
            days > 0
              ? `${days} day(s) later than baseline`
              : days < 0
                ? `${Math.abs(days)} day(s) earlier than baseline`
                : 'Same finish as baseline',
        ),
        baselineDurationValueColumn,
        durationColumn,
        varianceColumn(
          'baselineDurationVar',
          'Duration variance',
          'durationDays',
          'No baseline duration',
          (days) =>
            days > 0
              ? `${days} day(s) longer than baseline`
              : days < 0
                ? `${Math.abs(days)} day(s) shorter than baseline`
                : 'Same duration as baseline',
        ),
      ]
    : [startColumn, finishColumn];

  return [
    {
      name: 'assignSelect',
      label:
        '<input type="checkbox" class="gantt-assign-select-all" title="Select or deselect visible leaves (keeps other selections)" aria-label="Select or deselect visible leaves" />',
      align: 'center',
      width: resolveGanttColumnWidth(
        'assignSelect',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.assignSelect,
      ),
      min_width: 36,
      max_width: 52,
      resize: false,
      template: (task: any) => {
        const id = escapeHtml(String(task.id));
        const checked = selectedAssignTaskIdsRef.current.has(String(task.id))
          ? ' checked'
          : '';
        return (
          `<input type="checkbox" class="gantt-assign-select-checkbox" data-task-id="${id}"` +
          `${checked} aria-label="Select task for assign" />`
        );
      },
    },
    {
      name: 'srNo',
      label: 'Sr No',
      align: 'left',
      width: resolveGanttColumnWidth(
        'srNo',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.srNo,
      ),
      min_width: 72,
      resize: true,
      template: (task: any) => {
        const snap = store.getTask(String(task.id));
        const id = escapeHtml(String(task.id));
        const rawSrNo = snap?.srNo != null ? String(snap.srNo).trim() : '';
        // Non-breaking space keeps empty cells from collapsing under the absolute edit btn.
        const srNo = escapeHtml(rawSrNo || '\u00A0');
        return (
          `<div class="gantt-srno-cell">` +
          `<span class="gantt-srno-value">${srNo}</span>` +
          `<button type="button" class="gantt-task-edit-btn" data-task-id="${id}" title="Edit task details" aria-label="Edit task details">` +
          `&#9998;` +
          `</button></div>`
        );
      },
    },
    {
      name: 'text',
      label: 'Task name',
      width: resolveGanttColumnWidth(
        'text',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.text,
      ),
      min_width: 280,
      tree: true,
      resize: true,
      template: (task: any) => {
        const name = escapeHtml(resolveTaskName(task));
        const id = escapeHtml(String(task.id));
        const taskId = String(task.id);
        const hasChildren = parentIdsWithChildren.has(taskId);
        const isParent =
          hasChildren || task?.type === 'project' || !!store.getTask(taskId)?.summary;
        const deleteBtnClass = hasChildren
          ? 'gantt-task-delete-btn is-disabled'
          : 'gantt-task-delete-btn';
        const deleteTitle = hasChildren
          ? 'Cannot delete: task has child tasks'
          : 'Delete this task';
        const addBtn = canTaskCreate
          ? `<button type="button" class="gantt-task-add-btn" data-task-id="${id}" title="Add task here" aria-label="Add task here">+</button>`
          : `<button type="button" class="gantt-task-add-btn is-disabled" data-task-id="${id}" title="${escapeHtml(PERMISSION_RESTRICTED_MESSAGE)}" aria-label="Add task here" disabled>+</button>`;
        const deleteBtn = canTaskDelete
          ? `<button type="button" class="${deleteBtnClass}" data-task-id="${id}" title="${escapeHtml(deleteTitle)}" aria-label="Delete this task"${hasChildren ? ' disabled' : ''}>−</button>`
          : `<button type="button" class="gantt-task-delete-btn is-disabled" data-task-id="${id}" title="${escapeHtml(PERMISSION_RESTRICTED_MESSAGE)}" aria-label="Delete this task" disabled>−</button>`;
        const nameHtml = wrapPendingChangeCell(
          !!pendingEditedFieldsRef.current.get(taskId)?.has('text'),
          name,
        );
        const storeTask = store.getTask(taskId);
        const openIssueCount = Number(storeTask?.openIssueCount ?? 0);
        const hasAffectedIssue = Array.isArray(storeTask?.linkedIssues)
          ? storeTask.linkedIssues.some((issue) => issue.currentlyAffected)
          : false;
        const issueCountBtn =
          openIssueCount > 0
            ? `<button type="button" class="gantt-task-issue-count${hasAffectedIssue ? ' is-affected' : ''}" data-task-id="${id}" title="${escapeHtml(`${openIssueCount} open issue${openIssueCount === 1 ? '' : 's'}`)}" aria-label="${escapeHtml(`${openIssueCount} open issue${openIssueCount === 1 ? '' : 's'}`)}">` +
              `<svg class="gantt-task-issue-count-icon" width="11" height="11" viewBox="0 0 16 16" aria-hidden="true" focusable="false">` +
              `<path fill="currentColor" d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z"/>` +
              `</svg>` +
              `<span class="gantt-task-issue-count-num">${openIssueCount}</span>` +
              `</button>`
            : '';
        return (
          `<div class="gantt-task-name-cell">` +
          `<span class="gantt-task-name-text${isParent ? ' is-parent' : ''}" title="Double-click to rename">${nameHtml}</span>` +
          `<span class="gantt-task-name-trailing">` +
          `<span class="gantt-task-row-actions">` +
          addBtn +
          deleteBtn +
          `</span>` +
          issueCountBtn +
          `</span></div>`
        );
      },
      editor: { type: 'text', map_to: 'text' },
    },
    ...scheduleCoreColumns,
    ...(visibleOptionalColumns.wbs ? [{
      name: 'wbs',
      label: 'WBS',
      width: resolveGanttColumnWidth(
        'wbs',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.wbs,
      ),
      align: 'left',
      resize: true,
      template: (task: any) => {
        const inst = ganttRef.current?.instance;
        const code = inst?.getWBSCode ? inst.getWBSCode(task) : '';
        return escapeHtml(String(code ?? ''));
      },
    }] : []),
    ...(!baselineCompare && visibleOptionalColumns.duration ? [durationColumn] : []),
    ...(visibleOptionalColumns.actualStart ? [{
      name: 'actualStart',
      label: 'Actual start',
      align: 'center',
      width: resolveGanttColumnWidth(
        'actualStart',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.actualStart,
      ),
      resize: true,
      template: (task: any) => {
        const snap = store.getTask(String(task.id));
        return formatGanttDateFromStored(snap?.actualStart ?? task.actualStart ?? null);
      },
    }] : []),
    ...(visibleOptionalColumns.actualFinish ? [{
      name: 'actualFinish',
      label: 'Actual finish',
      align: 'center',
      width: resolveGanttColumnWidth(
        'actualFinish',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.actualFinish,
      ),
      resize: true,
      template: (task: any) => {
        const snap = store.getTask(String(task.id));
        return formatGanttDateFromStored(snap?.actualFinish ?? task.actualFinish ?? null);
      },
    }] : []),
    ...(visibleOptionalColumns.actualDuration ? [{
      name: 'actualDuration',
      label: 'Actual duration',
      align: 'center',
      width: resolveGanttColumnWidth(
        'actualDuration',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.actualDuration,
      ),
      resize: true,
      template: (task: any) => {
        const snap = store.getTask(String(task.id));
        const value = snap?.actualDuration ?? task.actualDuration;
        const n = Number(value);
        return Number.isFinite(n) ? String(Math.round(n)) : '';
      },
    }] : []),
    {
      name: 'progress',
      label: 'Progress',
      align: 'center',
      width: resolveGanttColumnWidth(
        'progress',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.progress,
      ),
      resize: true,
      template: (task: any) => {
        const n = Number(task.progress ?? 0);
        const label = !Number.isFinite(n) ? '0%' : `${Math.round(n * 100)}%`;
        return wrapPendingChangeCell(
          !!pendingEditedFieldsRef.current.get(String(task.id))?.has('progress'),
          label,
        );
      },
    },
    ...(visibleOptionalColumns.critical ? [{
      name: "critical",
      label: "Critical",
      width: resolveGanttColumnWidth(
        'critical',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.critical,
      ),
      align: "center",
      resize: true,
      // MSP/backend snapshot.critical ∪ live DHTMLX isCriticalTask
      template: (task: any) => {
        const snapCritical = !!store.getTask(String(task.id))?.critical;
        let liveCritical = false;
        try {
          liveCritical = !!ganttRef.current?.instance?.isCriticalTask?.(task);
        } catch {
          liveCritical = false;
        }
        return snapCritical || liveCritical ? '⚠️' : '';
      },
    }] : []),
    ...(visibleOptionalColumns.taskType ? [{
      name: 'taskType',
      label: 'Task type',
      width: resolveGanttColumnWidth(
        'taskType',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.taskType,
      ),
      align: 'left',
      resize: true,
      template: (task: any) => {
        const effectiveType = normalizeTaskType(
          accumulatedChangesRef.current.get(task.id)?.taskType ?? task.taskType
        ) ?? '';
        const optionsHtml = TASK_TYPE_OPTIONS.map(
          (o) =>
            `<option value="${escapeHtml(o.key)}"${o.key === effectiveType ? ' selected' : ''}>${escapeHtml(o.label)}</option>`
        ).join('');
        const pending = pendingEditedFieldsRef.current.get(String(task.id))?.has('taskType')
          ? ' is-pending-change'
          : '';
        return `<select class="gantt-task-type-select${pending}" data-task-id="${escapeHtml(String(task.id))}" aria-label="Task type">${optionsHtml}</select>`;
      },
      /* Custom component: no default editor; changes handled by delegated change listener. */
    }] : []),
    ...(visibleOptionalColumns.locationType ? [{
      name: 'locationType',
      label: 'Location name',
      width: resolveGanttColumnWidth(
        'locationType',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.locationType,
      ),
      align: 'left',
      resize: true,
      template: (task: any) => {
        const taskId = String(task.id);
        const snap = store.getTask(taskId);
        const ownLocId =
          snap?.locationId != null && String(snap.locationId).trim() !== ''
            ? String(snap.locationId).trim()
            : '';
        const locationId =
          ownLocId ||
          effectiveLocationIdByTaskIdRef.current.get(task.id) ||
          task.locationId ||
          '';
        // Own location: prefer task label; inherited: always resolve from effective id
        // so parent location edits update descendants without a full page refresh.
        const name = ownLocId
          ? task.locationType ||
            locationIdToNameRef.current.get(ownLocId) ||
            '-'
          : (locationId
              ? locationIdToNameRef.current.get(String(locationId)) ?? null
              : null) ||
            task.locationType ||
            '-';
        const safe = escapeHtml(String(name));
        const id = escapeHtml(taskId);
        const isActive =
          locationModalTaskIdRef.current != null &&
          taskId === String(locationModalTaskIdRef.current);
        const className = isActive ? 'gantt-open-location gantt-open-location-active' : 'gantt-open-location';
        const label = wrapPendingChangeCell(
          !!pendingEditedFieldsRef.current.get(taskId)?.has('locationType'),
          safe,
        );
        return `<span class="${className}" data-task-id="${id}" role="button" tabindex="-1">${label}</span>`;
      },
      // editor: { type: 'text', map_to: 'locationType' },
      /* Editor is only used to make the cell clickable; actual editing is handled via location tree panel. */
    }] : []),
    ...(visibleOptionalColumns.predecessors ? [{
      name: 'predecessors',
      label: 'Predecessor',
      width: resolveGanttColumnWidth(
        'predecessors',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.predecessors,
      ),
      min_width: 120,
      align: 'left',
      resize: true,
      editor: { type: 'predecessor', map_to: 'auto' },
      template: (task: any) => {
        const taskId = String(task.id);
        try {
          let predLinks = predSuccLinkIndex.byTarget.get(taskId) ?? [];
          if (predLinks.length === 0) {
            const gantt = ganttRef.current?.instance as {
              getLinks?: () => Array<{
                source: string | number;
                target: string | number;
                type?: string | number;
                lag?: number | null;
              }>;
            } | undefined;
            if (gantt?.getLinks) {
              predLinks = gantt
                .getLinks()
                .filter((l) => String(l.target) === taskId)
                .map((l) => ({
                  id: 0,
                  source: String(l.source),
                  target: String(l.target),
                  type: String(l.type ?? '0'),
                  lag: l.lag ?? null,
                }));
            }
          }
          const text = predLinks
            .map((link) => {
              const snap = store.getTask(link.source);
              const code = snap
                ? (snap.srNo != null && String(snap.srNo).trim()) || resolveTaskName(snap)
                : (() => {
                    const gantt = ganttRef.current?.instance as {
                      getTask?: (id: string | number) => unknown;
                      isTaskExists?: (id: string | number) => boolean;
                    } | undefined;
                    if (ganttRef.current?.instance?.isTaskExists?.(link.source)) {
                      return resolveTaskName(ganttRef.current?.instance?.getTask?.(link.source) as any);
                    }
                    return link.source;
                  })();
              return formatDependencyLinkToken(String(code || link.source), link.type, link.lag);
            })
            .filter(Boolean)
            .join(', ');
          const display = text || '—';
          const isActive =
            predecessorPickerTaskIdRef.current != null &&
            String(task.id) === String(predecessorPickerTaskIdRef.current);
          const className = isActive
            ? 'gantt-open-predecessors gantt-open-predecessors-active'
            : 'gantt-open-predecessors';
          const label = wrapPendingChangeCell(
            !!pendingEditedFieldsRef.current.get(taskId)?.has('predecessors'),
            escapeHtml(display),
          );
          return (
            `<span class="${className}" data-task-id="${escapeHtml(taskId)}">` +
            `<span class="gantt-predecessor-value">${label}</span>` +
            `<button type="button" class="gantt-predecessor-pick-btn" data-task-id="${escapeHtml(taskId)}" ` +
            `title="Browse tasks" aria-label="Browse predecessor tasks">` +
            `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">` +
            `<path d="M10 3H3v7h7V3zM21 3h-7v7h7V3zM21 14h-7v7h7v-7zM10 14H3v7h7v-7z"/>` +
            `</svg></button></span>`
          );
        } catch {
          return '';
        }
      },
      /* Single-click value: DHTMLX predecessor editor. Pick button: tree picker. */
    }] : []),
    ...(visibleOptionalColumns.successors ? [{
      name: 'successors',
      label: 'Successor',
      width: resolveGanttColumnWidth(
        'successors',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.successors,
      ),
      align: 'left',
      resize: true,
      template: (task: any) => {
        const taskId = String(task.id);
        try {
          let succLinks = predSuccLinkIndex.bySource.get(taskId) ?? [];
          if (succLinks.length === 0) {
            const gantt = ganttRef.current?.instance as {
              getLinks?: () => Array<{
                source: string | number;
                target: string | number;
                type?: string | number;
                lag?: number | null;
              }>;
            } | undefined;
            if (gantt?.getLinks) {
              succLinks = gantt
                .getLinks()
                .filter((l) => String(l.source) === taskId)
                .map((l) => ({
                  id: 0,
                  source: String(l.source),
                  target: String(l.target),
                  type: String(l.type ?? '0'),
                  lag: l.lag ?? null,
                }));
            }
          }
          const text = succLinks
            .map((link) => {
              const snap = store.getTask(link.target);
              const code = snap
                ? (snap.srNo != null && String(snap.srNo).trim()) || resolveTaskName(snap)
                : (() => {
                    const gantt = ganttRef.current?.instance as {
                      getTask?: (id: string | number) => unknown;
                      isTaskExists?: (id: string | number) => boolean;
                    } | undefined;
                    if (ganttRef.current?.instance?.isTaskExists?.(link.target)) {
                      return resolveTaskName(ganttRef.current?.instance?.getTask?.(link.target) as any);
                    }
                    return link.target;
                  })();
              return formatDependencyLinkToken(String(code || link.target), link.type, link.lag);
            })
            .filter(Boolean)
            .join(', ');
          return wrapPendingChangeCell(
            !!pendingEditedFieldsRef.current.get(taskId)?.has('successors'),
            escapeHtml(text),
          );
        } catch {
          return '';
        }
      },
    }] : []),
    ...(visibleOptionalColumns.tracked ? [{
      name: 'unitActivityTracked',
      label: 'Tracked',
      width: resolveGanttColumnWidth(
        'unitActivityTracked',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.unitActivityTracked,
      ),
      align: 'center',
      resize: true,
      template: (task: any) => {
        if (!trackedLeafTaskIds.has(String(task.id))) return '';
        return (
          '<span class="gantt-unit-activity-tracked-icon" role="img" aria-label="Tracked as unit activity">' +
          '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">' +
          '<circle cx="12" cy="12" r="10" fill="currentColor" opacity="0.22"/>' +
          '<circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="1.75"/>' +
          '<path d="M8.2 12.4l2.4 2.4 5.2-5.4" stroke="currentColor" stroke-width="2" ' +
          'stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
          '</svg></span>'
        );
      },
    }] : []),
    ...(visibleOptionalColumns.assignees ? [{
      name: 'assignees',
      label: 'Assignees',
      width: resolveGanttColumnWidth(
        'assignees',
        columnWidthsRef.current,
        projectId,
        DEFAULT_GANTT_COLUMN_WIDTHS.assignees,
      ),
      min_width: 90,
      resize: true,
      template: (task: any) => {
        const taskId = String(task.id);
        const locId =
          String(store.getTask(taskId)?.locationId ?? '').trim() ||
          String(effectiveLocationIdByTaskIdRef.current.get(taskId) ?? '').trim();
        const rows = resolveAssignmentsForTask({
          taskId,
          effectiveLocationId: locId || null,
          assignments: liveAssignmentsRef.current,
          locationNameById: locationIdToNameRef.current,
        });
        const summary = formatTaskAssigneesSummary(rows, { maxNames: 6 });
        if (rows.length === 0) {
          return (
            `<span class="gantt-assignees-cell is-empty" data-task-id="${escapeHtml(taskId)}" ` +
            `role="button" tabindex="-1" title="Assign users">—</span>`
          );
        }
        const avatars = buildAssigneeAvatarsHtml(rows, escapeHtml, { maxAvatars: 4 });
        return (
          `<span class="gantt-assignees-cell" data-task-id="${escapeHtml(taskId)}" ` +
          `role="button" tabindex="-1" title="${escapeHtml(summary)}">${avatars}</span>`
        );
      },
    }] : []),

  ];
}
